/**
 * Spis sekcji ustawień — **jedyne miejsce, w którym się je dokłada**.
 *
 * ⭐ Ustawienia są zaprojektowane pod rozbudowę, bo to jest miejsce, do którego trafia
 * wszystko, co w produkcie się konfiguruje: dziś organizacja i konto, jutro słownik typów,
 * progi, integracje. Dołożenie sekcji = **jeden wpis tutaj i jedna trasa** — nagłówek,
 * zaznaczenie w nawigacji i widoczność wynikają z tej tablicy.
 *
 * Konfiguracja mieszka w `model/`, a nie w komponencie, bo to **dane, nie widok**:
 * komponent eksportujący tablicę psuje odświeżanie na gorąco i miesza dwie role w pliku.
 */

import { ROLES } from '@/features/auth';

export interface SettingsSection {
  /** Odcinek ścieżki pod `/settings`. Adres jest częścią produktu (spec 4A). */
  path: string;
  labelKey: string;
  /**
   * Role, dla których sekcja jest widoczna. **Brak listy = dla każdego zalogowanego.**
   *
   * ⚠ Sekcje są **ukrywane, nie blokowane**. Kontrolę trzyma serwer (polityki na `/api/*`) —
   * tu chodzi o to, żeby nie pokazywać drzwi, które i tak są zamknięte.
   *
   * ⭐ **Admina dopisuje `canSee`, nie ta tablica** — dokładnie tak, jak po stronie serwera robi
   * to `WithAdmin`. Wypisywanie admina przy każdej sekcji byłoby okazją, żeby raz go pominąć,
   * a wtedy administrator nie zobaczyłby ekranu, którym ma zarządzać (decyzja 81).
   */
  roles?: readonly string[];
}

export interface SettingsGroup {
  labelKey: string;
  sections: readonly SettingsSection[];
}

/*
 * ⭐ Dwa spojrzenia na jedną tablicę, i oba są potrzebne:
 *
 * `GROUPS` trzyma **literały** (`as const`), bo tylko z nich da się wyliczyć
 * `SettingsSectionPath` — typ, który pilnuje, że każda sekcja ma ekran.
 * `SETTINGS_GROUPS` wystawia je pod **interfejsem**, bo konsumenci czytają `roles`,
 * a we wersji literalnej wpisy bez tego pola po prostu go nie mają (unia rozjeżdża się
 * na pięć różnych kształtów). ⚠ Próba obsłużenia obu potrzeb jedną deklaracją kończy się
 * błędem `TS2339` na `section.roles` — sprawdzone 2026-08-06.
 */
const GROUPS = [
  {
    labelKey: 'settings.group.organisation',
    sections: [
      { path: 'users', labelKey: 'settings.section.users', roles: [ROLES.admin] },
      // Bez `roles`: listę zespołów widzi każdy zalogowany (spec 9.2 — „kto jest
      // w jakim zespole, to wiedza organizacyjna, nie sekret"). Ukryte są same akcje.
      { path: 'teams', labelKey: 'settings.section.teams' },
      { path: 'invite', labelKey: 'settings.section.invite', roles: [ROLES.admin] },
    ],
  },
  {
    // Nowa grupa, a nie dopisanie do „Organizacji": słownik typów nie jest sprawą ludzi
    // i zespołów, tylko konfiguracją produktu. To pierwszy wpis, który udowadnia, że powłoka
    // ustawień rośnie tak, jak obiecywała decyzja 78 — jeden wpis i jedna trasa.
    labelKey: 'settings.group.product',
    sections: [
      { path: 'element-types', labelKey: 'settings.section.elementTypes', roles: [ROLES.architect] },
      // ⭐ Import mieszka w USTAWIENIACH, nie w menu roboczym (user 2026-08-08). To robota
      // admina wykonywana kilka razy w roku, a nie lista, którą się odwiedza — a wejście
      // w ustawienia jest z założenia wyjściem z pracy (decyzja 78).
      { path: 'import', labelKey: 'settings.section.import', roles: [ROLES.admin] },
  // ⚠ Architekt ORAZ admin (decyzja 108) — nadawanie właściciela jest ich rolą,
  // a lista robocza jest tylko innym wejściem do tej samej czynności.
  {
    path: 'ownerless',
    labelKey: 'settings.section.ownerless',
    roles: [ROLES.admin, ROLES.architect],
  },
    ],
  },
  {
    labelKey: 'settings.group.personal',
    sections: [{ path: 'account', labelKey: 'settings.section.account' }],
  },
] as const satisfies readonly SettingsGroup[];

export const SETTINGS_GROUPS: readonly SettingsGroup[] = GROUPS;

/**
 * Wszystkie odcinki ścieżek sekcji jako typ.
 *
 * ⭐ Po to istnieje: `SETTINGS_SECTION_ROUTES` mapuje ten typ na ekrany **rekordem
 * wyczerpującym**, więc dopisanie sekcji bez ekranu nie kompiluje się. Obietnica z nagłówka
 * tego pliku („jeden wpis tutaj i jedna trasa") była do 2026-08-06 nieprawdziwa — sekcje
 * były wypisane jeszcze dwa razy, w `routes.tsx` i w teście ustawień, a rozjazd wychodził
 * dopiero na ekranie.
 */
export type SettingsSectionPath = (typeof GROUPS)[number]['sections'][number]['path'];
